import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Center,
  SimpleGrid,
  useToast,
  Heading,
  Text
} from '@chakra-ui/react';
import { secureFetch } from '../data/secureFetch';
import { Helmet } from 'react-helmet-async';

const shareTitle = 'Contact PurelyGive | Nonprofit Fundraising Support';
const shareDescription = 'Schedule a demo or send a message to PurelyGive for zero-fee fundraising help, onboarding, and nonprofit donation platform support.';
const shareImage = `https://${window.location.host}/images/share-image.png`;
const shareUrl = `https://${window.location.host}/contact`;

const calendlyWidgetUrl = 'https://calendly.com/sam-purelygive/demo?hide_event_type_details=1&hide_gdpr_banner=1';

const ContactForm = () => {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const scriptId = 'calendly-widget-script';
    const existingScript = document.getElementById(scriptId);

    if (!existingScript) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
      return;
    }

    if (window.Calendly?.initInlineWidgets) {
      window.Calendly.initInlineWidgets();
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await secureFetch('/api/contact', {
        method: 'POST',
        data: form,
      });
      if (!response.ok) {
        throw new Error(response.error || 'Failed to send message');
      }
      toast({
        title: 'Message sent',
        description: 'Thank you—we’ll be in touch shortly.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{shareTitle}</title>
        <meta name="description" content={shareDescription} />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="PurelyGive" />
        <meta property="og:title" content={shareTitle} />
        <meta property="og:description" content={shareDescription} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:url" content={shareUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={shareTitle} />
        <meta name="twitter:description" content={shareDescription} />
        <meta name="twitter:image" content={shareImage} />
      </Helmet>
      <Box mt={16} px={{ base: 4, md: 6 }}>
        <Center>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} maxW="1100px" w="full" alignItems="start">
            <Box bg="white" p={{ base: 6, md: 8 }} borderRadius="xl" shadow="md">
              <Heading size="lg" mb={4}>Talk to our team</Heading>
              <Text mb={6}>Book a quick walkthrough instantly, or send us a message and we&apos;ll follow up.</Text>
              <Box
                className="calendly-inline-widget"
                data-url={calendlyWidgetUrl}
                style={{ minWidth: '320px', height: '700px' }}
              />
            </Box>

            <Box bg="white" p={{ base: 6, md: 8 }} borderRadius="xl" shadow="md">
              <Heading size="lg" mb={4}>Send us a message</Heading>
              <Text mb={6}>Have questions about setup, migration, or pricing? We&apos;re here to help.</Text>
              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Name</FormLabel>
                    <Input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Message</FormLabel>
                    <Textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="What can we help you with?"
                      rows={6}
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    colorScheme="orange"
                    isFullWidth
                    isLoading={loading}
                  >
                    Send Message
                  </Button>
                </VStack>
              </form>
            </Box>
          </SimpleGrid>
        </Center>
      </Box>
    </>
  );
};

export default ContactForm;
